import '@theme/swiper';

// Own initialization so navigation is ready before the first drag or click.
const selector = '.complementary-products-container';
const instances = new Map();

function closeDropdowns(container, except) {
  container.querySelectorAll('.custom-dropdown.dropdown-open').forEach((dropdown) => {
    if (dropdown === except) return;
    dropdown.classList.remove('dropdown-open', 'open-up');
    const toggle = dropdown.querySelector('.dropdown-toggle');
    toggle?.classList.remove('open');
    toggle?.setAttribute('aria-expanded', 'false');
    const options = dropdown.querySelector('.dropdown-options');
    options?.classList.remove('open');
    options?.classList.add('hidden');
  });
}

function setup(container) {
  if (instances.has(container)) return;
  const slider = container.querySelector('swiper-container');
  if (!slider) return;
  const count = slider.querySelectorAll('swiper-slide').length;
  const prev = container.querySelector('.swiper-button-prev');
  const next = container.querySelector('.swiper-button-next');
  const pagination = container.querySelector('.swiper-pagination');
  const events = new AbortController();
  const { signal } = events;

  Object.assign(slider, {
    slidesPerView: count > 1 ? 1.08 : 1,
    spaceBetween: 16,
    breakpointsBase: 'container',
    breakpoints: { 640: { slidesPerView: count > 1 ? 2 : 1, spaceBetween: 24 } },
    loop: false,
    speed: matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 350,
    watchOverflow: true,
    noSwipingSelector: 'button, input, label, select, .dropdown-options',
    // External buttons are bound below; do not let the navigation module also
    // attach handlers or defer their initial state until the first interaction.
    navigation: false,
    pagination: count > 1 ? { el: pagination, clickable: true } : false,
  });
  [prev, next].forEach((button) => button?.setAttribute('aria-controls', slider.id));
  slider.initialize();
  container.dataset.complementaryInit = 'true';

  const syncNavigation = () => {
    const swiper = slider.swiper;
    const unavailable = !swiper?.initialized || swiper.destroyed || !swiper.enabled;
    const locked = unavailable || swiper.isLocked;
    [[prev, locked || swiper?.isBeginning], [next, locked || swiper?.isEnd]].forEach(([button, disabled]) => {
      if (!button) return;
      button.disabled = Boolean(disabled);
      button.setAttribute('aria-disabled', String(Boolean(disabled)));
      button.classList.toggle('swiper-button-disabled', Boolean(disabled));
      button.classList.toggle('swiper-button-lock', Boolean(locked));
    });
  };
  [[prev, 'slidePrev'], [next, 'slideNext']].forEach(([button, method]) => {
    button?.addEventListener('click', (event) => {
      event.preventDefault();
      const swiper = slider.swiper;
      if (!swiper?.initialized || swiper.destroyed || button.disabled) return;
      closeDropdowns(container);
      swiper[method]();
      syncNavigation();
    }, { signal });
  });
  ['init', 'slidechange', 'toedge', 'fromedge', 'lock', 'unlock', 'resize', 'update', 'breakpoint', 'enable', 'disable'].forEach((name) => {
    slider.addEventListener(`swiper${name}`, syncNavigation, { signal });
  });
  // Initialize synchronously, then remeasure after the browser's first layout.
  syncNavigation();
  const refreshNavigation = () => {
    if (!slider.swiper || slider.swiper.destroyed) return;
    slider.swiper.update();
    syncNavigation();
  };
  const navigationFrame = requestAnimationFrame(refreshNavigation);
  window.addEventListener('load', refreshNavigation, { once: true, signal });

  // Preserve the product form's radio/change handlers and only manage presentation.
  container.addEventListener('click', (event) => {
    const toggle = event.target.closest('.dropdown-toggle');
    if (toggle) closeDropdowns(container, toggle.closest('.custom-dropdown'));
  }, { capture: true, signal });
  container.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const dropdown = event.target.closest('.custom-dropdown.dropdown-open');
    if (!dropdown) return;
    closeDropdowns(container);
    dropdown.querySelector('.dropdown-toggle')?.focus();
  }, { signal });
  const syncExpanded = () => {
    container.querySelectorAll('.dropdown-toggle').forEach((toggle) => {
      toggle.setAttribute('aria-expanded', String(toggle.classList.contains('open')));
    });
  };
  const dropdownObserver = new MutationObserver(syncExpanded);
  dropdownObserver.observe(slider, { subtree: true, attributes: true, attributeFilter: ['class'] });
  syncExpanded();
  ['swiperslidechange', 'swipersliderfirstmove', 'swiperbreakpoint'].forEach((name) => {
    slider.addEventListener(name, () => closeDropdowns(container), { signal });
  });

  instances.set(container, () => {
    cancelAnimationFrame(navigationFrame);
    events.abort();
    dropdownObserver.disconnect();
    slider.swiper?.destroy(true, true);
    delete container.dataset.complementaryInit;
    instances.delete(container);
  });
}

function initAll(root = document) {
  if (root.matches?.(selector)) setup(root);
  root.querySelectorAll(selector).forEach(setup);
}

initAll();
document.addEventListener('shopify:section:load', (event) => initAll(event.target));
document.addEventListener('shopify:block:select', (event) => initAll(event.target.parentElement || document));
document.addEventListener('shopify:section:unload', (event) => {
  instances.forEach((cleanup, container) => {
    if (event.target.contains(container)) cleanup();
  });
});
